import { iso, Newtype } from 'newtype-ts';
import * as Iso from 'monocle-ts/Iso';
import * as Eq from 'fp-ts/Eq';
import * as Ord from 'fp-ts/Ord';

/**
 * A summary of a logical "item" the user may be trying to purchase
 *
 * This is a concept that appears in several places - usually when showing
 * some sort of item to the user. In some cases this might be an individual
 * product, in which case these are just the details of the product itself.
 * Catalogue entries are essentially just a summary with a pointer to a specific
 * configuration of a configuration space, and the corresponding summary should
 * be representative of the specific configuration used.
 * A furniture item also often needs a summary - but this doesn't necessarily
 * correspond to either the original catalogue entry or any specific product
 * within it.
 */
type _Summary = {
    name: string;
    image: string;
};

export interface Summary extends Newtype<{ readonly Summary: unique symbol }, _Summary> {}

const _ = iso<Summary>();

export const create = _.wrap;

const buildLens = <A extends keyof _Summary>(p: A) => Iso.prop<_Summary, A>(p)(_);

const nameL = buildLens('name');
const imageL = buildLens('image');

export const getName = nameL.get;
export const getImage = imageL.get;

export const getNameOr = (dflt: string, summary: Summary | undefined) =>
    summary ? getName(summary) : dflt;

export const eqSummaryByName: Eq.Eq<Summary> = {
    equals(x, y) {
        return getName(x) === getName(y);
    },
};

export const equalsByName = eqSummaryByName.equals;
